import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Radio, Trophy, BookOpen } from "lucide-react";
import { api, fmtL, fmtTime } from "@/lib/api";
import { StatusPill, TeamBadge, PlayerAvatar, Spinner } from "@/components/opl/Common";

function Countdown({ at }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const d = new Date(at).getTime() - now;
  if (!at || isNaN(d) || d <= 0) return null;
  const parts = [[Math.floor(d / 864e5), "Days"], [Math.floor(d / 36e5) % 24, "Hrs"], [Math.floor(d / 6e4) % 60, "Min"], [Math.floor(d / 1e3) % 60, "Sec"]];
  return (
    <div className="flex gap-3 mt-10" data-testid="auction-countdown">
      {parts.map(([v, l]) => (
        <div key={l} className="bg-white border border-[#E7E5E0] rounded-2xl px-4 py-3 text-center min-w-[76px] shadow-sm">
          <div className="num text-3xl font-bold text-[#0F172A]">{String(v).padStart(2, "0")}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{l}</div>
        </div>
      ))}
    </div>
  );
}

function Hero({ d }) {
  return (
    <section className="relative bg-[#06102A]" data-testid="home-hero">
      <h1 className="sr-only">OPL-6 Oneiros Premier League</h1>
      <img src="/cover.jpg" alt="OPL-6 Oneiros Premier League cover" className="block w-full h-auto" data-testid="hero-cover-image" />
      <div className="bg-[#06102A] border-t border-[#D4AF37]/25">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <p className="text-white/80 text-sm sm:text-base max-w-xl">The interclub cricket tournament of Oneiros Sports Club — and the player auction where every franchise builds its squad, one bid at a time.</p>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/live" className="gold-bg px-6 py-3 rounded-full font-semibold flex items-center gap-2 hover:brightness-110 transition-[filter]" data-testid="hero-live-btn"><Radio className="h-4 w-4" />Watch live auction</Link>
            <Link to="/rules" className="px-6 py-3 rounded-full border border-white/30 bg-white/10 backdrop-blur-md font-semibold text-white hover:bg-white/20 transition-colors flex items-center gap-2" data-testid="hero-rules-btn"><BookOpen className="h-4 w-4" />Read the rulebook</Link>
            <StatusPill status={d.status} />
          </div>
        </div>
        {d.auction_scheduled_at && <div className="max-w-7xl mx-auto px-5 lg:px-8 pb-6"><Countdown at={d.auction_scheduled_at} /></div>}
      </div>
    </section>
  );
}

function Stats({ s }) {
  const items = [["Teams", s.teams], ["Players", s.total_players], ["Sold", s.sold], ["Unsold", s.unsold], ["In pool", s.remaining], ["Total spend", fmtL(s.total_spent)]];
  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-8 mt-12">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 bg-white border border-[#E7E5E0] rounded-3xl overflow-hidden shadow-sm" data-testid="home-stats">
        {items.map(([l, v]) => (
          <div key={l} className="p-5 border-r border-b lg:border-b-0 border-[#F0EEE8]">
            <div className="text-[11px] uppercase tracking-widest text-slate-500">{l}</div>
            <div className="num text-2xl font-bold mt-1 text-[#0F172A]">{v}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FeaturedTeams({ teams }) {
  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-8 mt-24">
      <div className="flex items-end justify-between mb-8">
        <div><p className="eyebrow">Franchises</p><h2 className="text-3xl sm:text-4xl font-bold mt-2 text-[#0F172A]">Featured teams</h2></div>
        <Link to="/teams" className="text-sm font-semibold text-amber-700 flex items-center gap-1 hover:gap-2 transition-[gap]" data-testid="home-all-teams">All teams <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {teams.slice(0, 6).map((t, i) => (
          <div key={t.id} className="card-l p-6 rise" style={{ animationDelay: `${i * 0.05}s` }} data-testid={`home-team-${t.code}`}>
            <div className="flex items-center gap-4">
              <TeamBadge team={t} size={54} />
              <div className="min-w-0"><h3 className="font-semibold text-lg truncate text-[#0F172A]">{t.name}</h3><p className="text-sm text-slate-500 truncate">Owner · {t.owner_name}</p></div>
            </div>
            <div className="flex justify-between mt-6 pt-4 border-t border-[#F0EEE8] text-sm">
              <span className="text-slate-500">Squad <b className="text-[#0F172A] num">{t.players_count}</b></span>
              {t.remaining_purse !== undefined && <span className="text-slate-500">Purse left <b className="text-amber-700 num">{fmtL(t.remaining_purse)}</b></span>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function LatestAndUpdates({ d }) {
  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-8 mt-24 grid lg:grid-cols-5 gap-8">
      <div className="lg:col-span-3">
        <p className="eyebrow">Hammer down</p><h2 className="text-3xl font-bold mt-2 mb-6 text-[#0F172A]">Latest sold players</h2>
        <div className="space-y-2" data-testid="home-latest-sold">
          {d.latest_sold.map((p) => (
            <div key={p.id} className="card-l !rounded-2xl p-3 flex items-center gap-4">
              <PlayerAvatar player={p} size={44} />
              <div className="flex-1 min-w-0"><div className="font-semibold truncate text-[#0F172A]">{p.name}</div><div className="text-xs text-slate-500">{p.role} → {p.team_name}</div></div>
              <div className="num font-bold text-amber-700">{fmtL(p.sold_price)}</div>
            </div>
          ))}
          {!d.latest_sold.length && <p className="text-slate-500 card-l p-6">No players sold yet. The hammer hasn't fallen.</p>}
        </div>
      </div>
      <div className="lg:col-span-2 space-y-6">
        <div className="rounded-3xl p-7 bg-[#0F172A] text-white relative overflow-hidden" data-testid="home-highest">
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-amber-400/20 blur-2xl" />
          <Trophy className="h-6 w-6 text-amber-400" />
          <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-amber-300 mt-4">Highest purchase</p>
          {d.highest ? <><h3 className="text-2xl font-bold mt-2">{d.highest.player}</h3><p className="text-slate-400">{d.highest.team}</p><p className="num text-4xl font-extrabold shine mt-3">{fmtL(d.highest.price)}</p></> :
            <p className="text-slate-400 mt-2">To be decided at the auction</p>}
        </div>
        <div className="card-l p-6 hover:!transform-none" data-testid="home-updates">
          <p className="eyebrow mb-3">Tournament updates</p>
          {d.updates.map((u) => <div key={u.id} className="py-2 border-b border-[#F0EEE8] text-sm"><p className="text-slate-700">{u.message}</p><p className="text-xs text-slate-400">{fmtTime(u.created_at)}</p></div>)}
          {!d.updates.length && <p className="text-sm text-slate-500">No updates yet</p>}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/public/home").then((r) => setD(r.data)); }, []);
  if (!d) return <Spinner />;
  return (
    <div>
      <Hero d={d} />
      {d.stats && <Stats s={d.stats} />}
      <FeaturedTeams teams={d.teams} />
      <LatestAndUpdates d={d} />
    </div>
  );
}
